import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getAgeGroup, createAgeGroup, updateSingleAgeGroup, deleteAgeGroup } from '../../../../Redux/Actions/action';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import ButtonContained from '../../../Common/ButtonContained';
import CommonTextField from '../../../Common/CommonTextField';
import TextLabel from '../../../Common/CommonTextLabel';
import ErrorHandler from '../../../../Utils/handler';
import CustomDialog from '../../../Common/CustomDialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConfirm } from 'material-ui-confirm';

const initialFormData = {
    ageGroup: '',
    minAge: '',
    maxAge: '',
    managementCharge: 100,
    policyAmount: '',
    amount: ''
};

const AgeWisePayment = () => {
    const dispatch = useDispatch();
    const confirmDialog = useConfirm();
    
    const [ageGroups, setAgeGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = {
                ...prev,
                [name]: name === 'ageGroup' ? value : (value === '' ? '' : Number(value))
            };
            // Auto-calculate total amount when managementCharge or policyAmount changes
            if (name === 'managementCharge' || name === 'policyAmount') {
                const mgmt = name === 'managementCharge' ? (value === '' ? 0 : Number(value)) : (prev.managementCharge || 0);
                const policy = name === 'policyAmount' ? (value === '' ? 0 : Number(value)) : (prev.policyAmount || 0);
                updated.amount = mgmt + policy;
            }
            return updated;
        });
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        let newErrors = {};
        if (!formData.ageGroup?.trim()) newErrors.ageGroup = 'Age group name is required';
        if (formData.minAge === '' || formData.minAge < 0) newErrors.minAge = 'Valid min age is required (0 or above)';
        if (formData.maxAge === '' || formData.maxAge < 0) newErrors.maxAge = 'Valid max age is required (0 or above)';
        if (formData.managementCharge === '' || formData.managementCharge < 0) newErrors.managementCharge = 'Valid management charge is required';
        if (formData.policyAmount === '' || formData.policyAmount < 0) newErrors.policyAmount = 'Valid policy amount is required';
        if (formData.minAge !== '' && formData.maxAge !== '' && Number(formData.minAge) >= Number(formData.maxAge)) {
            newErrors.maxAge = 'Max age must be greater than min age';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const _getAgeGroup = async () => {
        setIsLoading(true);
        dispatch(showLoader(true));
        try {
            const res = await dispatch(getAgeGroup()).unwrap();
            
            // Handle cancelled requests
            if (res?.cancelled) {
                return;
            }
            
            // API response structure: { status, message, data: { ageGroup, code } }
            const data = res?.data || res;
            if (data?.code === 1 && data?.ageGroup) {
                setAgeGroups(data.ageGroup);
            } else if (data?.ageGroup) {
                setAgeGroups(data.ageGroup);
            }
        } catch (err) {
            ErrorHandler(err);
        } finally {
            setIsLoading(false);
            dispatch(showLoader(false));
        }
    };

    const _createAgeGroup = async () => {
        if (!validate()) return;
        
        dispatch(showLoader(true));
        try {
            const res = await dispatch(createAgeGroup({
                ageGroup: formData.ageGroup,
                minAge: Number(formData.minAge),
                maxAge: Number(formData.maxAge),
                managementCharge: Number(formData.managementCharge),
                policyAmount: Number(formData.policyAmount),
                amount: Number(formData.amount)
            })).unwrap();
            
            if (res?.code === 1) {
                toast.success('Age group created successfully');
                handleClose();
                _getAgeGroup();
            } else {
                toast.error(res?.message || 'Failed to create age group');
            }
        } catch (err) {
            ErrorHandler(err);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const _updateAgeGroup = async () => {
        if (!validate()) return;
        
        dispatch(showLoader(true));
        try {
            const res = await dispatch(updateSingleAgeGroup({
                _id: formData._id,
                ageGroup: formData.ageGroup,
                minAge: Number(formData.minAge),
                maxAge: Number(formData.maxAge),
                managementCharge: Number(formData.managementCharge),
                policyAmount: Number(formData.policyAmount),
                amount: Number(formData.amount)
            })).unwrap();
            
            if (res?.code === 1) {
                toast.success('Age group updated successfully');
                handleClose();
                _getAgeGroup();
            } else {
                toast.error(res?.message || 'Failed to update age group');
            }
        } catch (err) {
            ErrorHandler(err);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const _deleteAgeGroup = async (id, name) => {
        try {
            await confirmDialog({
                title: 'Delete Age Group',
                description: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
                confirmationText: 'Delete',
                cancellationText: 'Cancel',
            });
            
            dispatch(showLoader(true));
            const res = await dispatch(deleteAgeGroup(id)).unwrap();
            
            if (res?.code === 1) {
                toast.success('Age group deleted successfully');
                _getAgeGroup();
            } else {
                toast.error(res?.message || 'Failed to delete age group');
            }
        } catch (err) {
            if (err !== undefined) {
                ErrorHandler(err);
            }
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleOpen = (group = null) => {
        if (group) {
            setIsEdit(true);
            setFormData({
                _id: group._id,
                ageGroup: group.ageGroup,
                minAge: group.minAge,
                maxAge: group.maxAge,
                managementCharge: group.managementCharge || 100,
                policyAmount: group.policyAmount || (group.amount - 100),
                amount: group.amount
            });
        } else {
            setIsEdit(false);
            setFormData(initialFormData);
        }
        setErrors({});
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setIsEdit(false);
        setFormData(initialFormData);
        setErrors({});
    };

    useEffect(() => {
        _getAgeGroup();
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>
                            Age Wise Payment Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure payment amounts for ICICI payment integration based on member age
                        </Typography>
                    </Box>
                    <ButtonContained 
                        text="Add Age Group" 
                        onClick={() => handleOpen()}
                    />
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: '10px', mb: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="body2" color="primary">
                        <strong>Note:</strong> These age groups are used during member registration to calculate the payment amount based on the member's age. 
                        The amount will be automatically fetched during ICICI payment processing.
                    </Typography>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '10px' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#3E4094' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Age Group</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Age Range</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Mgmt Charge (₹)</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Policy Amt (₹)</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#fff' }}>Total (₹)</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#fff' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ageGroups.length > 0 ? (
                                ageGroups.map((group) => (
                                    <TableRow key={group._id || group.ageGroup} hover>
                                        <TableCell>
                                            <Typography fontWeight={500}>{group.ageGroup}</Typography>
                                            {group.ageGroupGuj && (
                                                <Typography variant="caption" color="text.secondary">{group.ageGroupGuj}</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={`${group.minAge} - ${group.maxAge} years`} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="text.secondary">
                                                ₹{(group.managementCharge || 100)?.toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="info.main">
                                                ₹{(group.policyAmount || (group.amount - 100))?.toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography fontWeight={600} color="success.main">
                                                ₹{group.amount?.toLocaleString('en-IN')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                color="primary" 
                                                onClick={() => handleOpen(group)}
                                                size="small"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton 
                                                color="error" 
                                                onClick={() => _deleteAgeGroup(group._id, group.ageGroup)}
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {isLoading ? 'Loading...' : 'No age groups configured. Click "Add Age Group" to create one.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            {/* Add/Edit Dialog */}
            <CustomDialog
                isOpen={open}
                handleClose={handleClose}
                title={isEdit ? 'Edit Age Group' : 'Add Age Group'}
                maxWidth="sm"
                action={
                    <ButtonContained 
                        text={isEdit ? 'Update' : 'Create'} 
                        onClick={isEdit ? _updateAgeGroup : _createAgeGroup}
                    />
                }
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextLabel title="Age Group Name" required />
                        <CommonTextField
                            name="ageGroup"
                            value={formData.ageGroup}
                            onChange={handleChange}
                            placeholder="e.g., Young Adults, Middle Age"
                            error={!!errors.ageGroup}
                            helperText={errors.ageGroup}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextLabel title="Min Age" required />
                        <CommonTextField
                            name="minAge"
                            type="number"
                            value={formData.minAge}
                            onChange={handleChange}
                            placeholder="e.g., 18"
                            error={!!errors.minAge}
                            helperText={errors.minAge}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextLabel title="Max Age" required />
                        <CommonTextField
                            name="maxAge"
                            type="number"
                            value={formData.maxAge}
                            onChange={handleChange}
                            placeholder="e.g., 30"
                            error={!!errors.maxAge}
                            helperText={errors.maxAge}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextLabel title="Management Charge (₹)" required />
                        <CommonTextField
                            name="managementCharge"
                            type="number"
                            value={formData.managementCharge}
                            onChange={handleChange}
                            placeholder="e.g., 100"
                            error={!!errors.managementCharge}
                            helperText={errors.managementCharge || "વાહિવટી ચાર્જ"}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextLabel title="Policy Amount (₹)" required />
                        <CommonTextField
                            name="policyAmount"
                            type="number"
                            value={formData.policyAmount}
                            onChange={handleChange}
                            placeholder="e.g., 2000"
                            error={!!errors.policyAmount}
                            helperText={errors.policyAmount || "સહયોગનિધિ"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextLabel title="Total Amount (₹)" />
                        <CommonTextField
                            name="amount"
                            type="number"
                            value={formData.amount}
                            onChange={handleChange}
                            placeholder="Auto-calculated"
                            disabled
                            helperText="કુલ વાર્ષિક ચુકવણી (auto-calculated)"
                        />
                    </Grid>
                </Grid>
            </CustomDialog>
        </Grid>
    );
};

export default AgeWisePayment;
